import React from "react";
import { cn } from "@/helpers/tailwind";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import { Button } from "@/components/ui";

interface HorizontalScrollListProps {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  fadeClassName?: string;
  showFades?: boolean;
  showArrows?: boolean;
}

const HorizontalScrollList: React.FC<HorizontalScrollListProps> = ({
  children,
  className,
  containerClassName,
  fadeClassName = "from-background",
  showFades = false,
  showArrows = false,
}) => {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [leftFadeOpacity, setLeftFadeOpacity] = React.useState(0);
  const [rightFadeOpacity, setRightFadeOpacity] = React.useState(1);
  const [hasOverflow, setHasOverflow] = React.useState(false);

  const checkOverflow = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setHasOverflow(container.scrollWidth > container.clientWidth);
  }, []);

  const handleScroll = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollWidth = container.scrollWidth;
    const containerWidth = container.clientWidth;
    const maxScroll = scrollWidth - containerWidth;
    const scrollLeft = container.scrollLeft;
    const scrollRight = maxScroll - scrollLeft;

    if (scrollLeft >= 10) setLeftFadeOpacity(1);
    else if (scrollLeft < 10) setLeftFadeOpacity(0);

    if (scrollRight >= 10) setRightFadeOpacity(1);
    else if (scrollRight < 10) setRightFadeOpacity(0);
  }, []);

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: -200, behavior: "smooth" });
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: 200, behavior: "smooth" });
  };

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(container);
    checkOverflow();

    return () => {
      container.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [handleScroll, checkOverflow]);

  const fadeClassNames = cn(
    "w-8 pointer-events-none absolute inset-y-0 to-transparent",
    "transition-opacity duration-500 ease-in-out z-[1]",
    { [fadeClassName]: showFades }
  );

  return (
    <div
      className={cn("relative flex-grow overflow-hidden", containerClassName)}>
      <div
        ref={scrollContainerRef}
        className={cn(
          "scrollbar-hide flex items-center gap-2 overflow-hidden overflow-x-auto",
          className
        )}>
        {children}
      </div>
      {/* scroll arrows */}
      {hasOverflow && showArrows && (
        <>
          <Button
            onClick={scrollLeft}
            className={cn(
              "absolute left-0 top-1/2 z-10 h-5 w-5 -translate-y-1/2 rounded-full bg-slate-400 p-0 text-slate-900 drop-shadow-[-4px_4px_10px_rgba(0,0,0,0.60)] filter hover:bg-white",
              { "opacity-0": leftFadeOpacity === 0 }
            )}>
            <ArrowLeftIcon />
          </Button>
          <Button
            onClick={scrollRight}
            className={cn(
              "absolute right-0 top-1/2 z-10 h-5 w-5 -translate-y-1/2 rounded-full bg-slate-400 p-0 text-slate-900 drop-shadow-[-4px_4px_10px_rgba(0,0,0,0.60)] filter hover:bg-white",
              { "opacity-0": rightFadeOpacity === 0 }
            )}>
            <ArrowRightIcon />
          </Button>
        </>
      )}
      {/* horizontal fades */}
      {showFades && (
        <>
          <div
            className={cn("left-0 bg-gradient-to-r", fadeClassNames, {
              "opacity-100": leftFadeOpacity === 1,
              "opacity-0": leftFadeOpacity === 0,
            })}
          />
          <div
            className={cn("right-0 bg-gradient-to-l", fadeClassNames, {
              "opacity-100": rightFadeOpacity === 1,
              "opacity-0": rightFadeOpacity === 0,
            })}
          />
        </>
      )}
    </div>
  );
};

export default HorizontalScrollList;
